/**
 * TanStack Query hooks for Analysis, Comparison, and Benchmarks
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { api } from '../client';
import type { AnalysisStatusResponse } from '../types';

// Query keys
export const analysisKeys = {
  all: ['analysis'] as const,
  deal: (dealId: number) => [...analysisKeys.all, dealId] as const,
  dealTermSheet: (dealId: number, termSheetId: number) =>
    [...analysisKeys.all, dealId, termSheetId] as const,
  status: (dealId: number, termSheetId?: number) =>
    termSheetId
      ? ([...analysisKeys.all, 'status', dealId, termSheetId] as const)
      : ([...analysisKeys.all, 'status', dealId] as const),
};

export const comparisonKeys = {
  all: ['comparison'] as const,
  // profile is part of the key so the in-tab profile switcher re-fetches (the
  // composite ranking is computed server-side per profile, v4).
  deal: (dealId: number, profile?: string | null) =>
    [...comparisonKeys.all, dealId, profile ?? 'default'] as const,
};

export const benchmarkKeys = {
  all: ['benchmarks'] as const,
  filtered: (filters: Record<string, string>) => [...benchmarkKeys.all, filters] as const,
  marketRates: () => [...benchmarkKeys.all, 'marketRates'] as const,
  mirRates: () => [...benchmarkKeys.all, 'mirRates'] as const,
};

// Get analysis for a deal
export function useAnalysis(dealId: number, termSheetId?: number) {
  return useQuery({
    queryKey: termSheetId
      ? analysisKeys.dealTermSheet(dealId, termSheetId)
      : analysisKeys.deal(dealId),
    queryFn: () => api.analysis.get(dealId, termSheetId),
    enabled: !!dealId,
    retry: (failureCount, error) => {
      // Don't retry 404s — deal has no analysis yet, retrying won't help
      if (isAxiosError(error) && error.response?.status === 404) {return false;}
      return failureCount < 2;
    },
  });
}

/**
 * Phase 0 T-002: Poll async scoring status.
 *
 * The dashboard uses this while the Celery `score_deal_task` runs so the
 * user sees skeleton -> scored without blocking on a synchronous request.
 *
 * Polling behaviour:
 *   - Every 1.5s while status is pending/running.
 *   - Stops (refetchInterval=false) on 'ready' or 'error'.
 *   - `enabled` flag lets the caller suppress polling when there's no
 *     deal yet or scoring has already resolved server-side.
 *
 * The caller is responsible for:
 *   - Invalidating analysisKeys.deal() / .dealTermSheet() once status
 *     flips to 'ready' so useAnalysis refetches fresh scores.
 *   - Surfacing error_message to the user on 'error'.
 */
export function useAnalysisStatus(
  dealId: number | null | undefined,
  options?: { enabled?: boolean; termSheetId?: number }
) {
  const { enabled = true, termSheetId } = options ?? {};
  return useQuery<AnalysisStatusResponse>({
    queryKey: analysisKeys.status(dealId ?? 0, termSheetId),
    queryFn: () => api.analysis.getStatus(dealId as number, termSheetId),
    enabled: enabled && !!dealId,
    // Poll every 1.5s while scoring is in flight; stop on terminal states.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'ready' || status === 'error') {return false;}
      return 1500;
    },
    refetchIntervalInBackground: false,
    // Status responses are cheap — don't hold stale data between polls.
    staleTime: 0,
    gcTime: 30 * 1000,
    retry: (failureCount, error) => {
      // Don't retry 404s — server says no analysis exists yet.
      if (isAxiosError(error) && error.response?.status === 404) {return false;}
      return failureCount < 2;
    },
  });
}

// Get comparison for a deal. `profile` (v4) overrides the user's set scoring
// profile for the in-tab lens switcher — the server recomputes the composite
// ranking under it; omit to honour the user's profile.
export function useComparison(dealId: number, profile?: string | null) {
  return useQuery({
    queryKey: comparisonKeys.deal(dealId, profile),
    queryFn: () => api.comparison.get(dealId, profile),
    enabled: !!dealId,
    // Keep the current comparison visible while a profile toggle re-fetches, so
    // the lens switch feels live instead of flashing to a skeleton (v4).
    placeholderData: keepPreviousData,
  });
}

// Get benchmarks
export function useBenchmarks(
  filters?: Record<string, string>,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: benchmarkKeys.filtered(filters || {}),
    queryFn: () => api.benchmarks.get(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes - benchmarks don't change often
    // Benchmarks are Pro-only, now enforced server-side (403 for Free). Gate the
    // request so Free users never fire a call that will be rejected.
    enabled: options?.enabled ?? true,
  });
}

// Get market rates
export function useMarketRates() {
  return useQuery({
    queryKey: benchmarkKeys.marketRates(),
    queryFn: () => api.benchmarks.getMarketRates(),
    // EURIBOR fixes once per business day at 11:00 CET — refetching inside a
    // single session never yields new numbers. 6h avoids churn across a
    // workday while still picking up the day's fix without a manual refresh.
    staleTime: 6 * 60 * 60 * 1000,
  });
}

// ECB MIR — Belgian new-business lending averages (the Ask briefing's market
// block). MIR is MONTHLY with a ~6 week publication lag; the server caches
// 24h, so a 24h client staleTime never misses a fresh observation.
export function useMirRates() {
  return useQuery({
    queryKey: benchmarkKeys.mirRates(),
    queryFn: () => api.benchmarks.getMirRates(),
    staleTime: 24 * 60 * 60 * 1000,
  });
}
